import AddBoxIcon from "@mui/icons-material/AddBox";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PostAddIcon from "@mui/icons-material/PostAdd";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import TableAddActions, { type TableAddAction } from "_shared/_views/tables/table-add-actions";
import { useState, type MouseEvent as ReactMouseEvent } from "react";
import { CREATE_FROM_TEMPLATE_LABEL } from "work/proposal-templates/_views/create-from-proposal-template-dialog";
import { CREATE_FROM_PREVIOUS_LABEL } from "work/proposals/drafts-from-previous/_views/create-from-previous-proposal-dialog";

export const CREATE_PROPOSAL_LABEL = "Create Proposal";
/** The narrow layout's dropdown, named for what it offers since it has no text of its own. */
export const MORE_WAYS_TO_CREATE_LABEL = "More ways to create a proposal";

const CompactGroup = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
}));

const CompactButton = styled(IconButton)(() => ({
  padding: 0,
}));

/**
 * The proposals table's way to start a proposal: one control, not a row of look-alike buttons.
 *
 * Create Proposal is the press. Create from Previous (#1569) and Create from Template (#6129) wait
 * behind the arrow beside it, as every other table's further ways to add do (`TableAddActions`).
 * Someone who may not create proposals sees Create Proposal alone and disabled, with the reason in
 * its tooltip, and no arrow: there is nothing behind it they could take.
 *
 * `compact` is the narrow layout, where the title row has room for an icon and not a label: the same
 * two choices, drawn as the + it always was and a small arrow.
 */
export default function CreateProposalSplitButton(
  props: Readonly<{
    compact: boolean;
    canCreate: boolean;
    /** Shown on Create Proposal while it is disabled, or while the reader's permissions are loading. */
    disabledReason?: string;
    onCreate: () => void;
    onCreateFromPrevious: () => void;
    onCreateFromTemplate: () => void;
  }>,
) {
  const { compact, canCreate, disabledReason, onCreate, onCreateFromPrevious, onCreateFromTemplate } = props;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  if (!compact) {
    const actions: TableAddAction[] = [
      { label: CREATE_PROPOSAL_LABEL, icon: <AddBoxIcon />, disabled: !canCreate, onClick: onCreate },
      ...(canCreate
        ? [
            { label: CREATE_FROM_PREVIOUS_LABEL, icon: <ContentCopyIcon fontSize="small" />, onClick: onCreateFromPrevious },
            { label: CREATE_FROM_TEMPLATE_LABEL, icon: <PostAddIcon fontSize="small" />, onClick: onCreateFromTemplate },
          ]
        : []),
    ];
    if (canCreate) return <TableAddActions actions={actions} />;
    return (
      <Tooltip title={disabledReason ?? ""}>
        {/* A span keeps the tooltip reachable while the button inside it is disabled. */}
        <span>
          <TableAddActions actions={actions} />
        </span>
      </Tooltip>
    );
  }

  return (
    <CompactGroup>
      <Tooltip title={canCreate ? CREATE_PROPOSAL_LABEL : (disabledReason ?? "")}>
        <span>
          <CompactButton color="primary" aria-label={CREATE_PROPOSAL_LABEL} disabled={!canCreate} onClick={onCreate}>
            <AddBoxIcon fontSize="large" />
          </CompactButton>
        </span>
      </Tooltip>
      {canCreate && (
        <>
          <CompactButton
            color="primary"
            aria-label={MORE_WAYS_TO_CREATE_LABEL}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            onClick={(event: ReactMouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget)}
          >
            <ArrowDropDownIcon />
          </CompactButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onCreateFromPrevious();
              }}
            >
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={CREATE_FROM_PREVIOUS_LABEL} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onCreateFromTemplate();
              }}
            >
              <ListItemIcon>
                <PostAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={CREATE_FROM_TEMPLATE_LABEL} />
            </MenuItem>
          </Menu>
        </>
      )}
    </CompactGroup>
  );
}
